import { Link } from "react-router-dom";
import { type ChannelListing, readinessLabel, readinessTone, approvalState } from "@/lib/channels";
import { publicationStateMeta } from "@/lib/listing-pricing";
import { approvalLabel, approvalTone, nextAction, providerLabel } from "@/lib/listing-action";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName } from "@/lib/connector-identity";
import { listingLiveLinks } from "@/lib/listing-live-display";
import { ViewLiveLink } from "@/components/feature/ViewLiveLink";
import { canonicalListingProductId, operatorDirtyDomains } from "@/lib/phase1-sync-contract";
import type { ListingChannelPriceSummary } from "@/lib/listing-channel-price-display";

interface ListingCardProps {
  listing: ChannelListing;
  readiness: string | undefined;
  confirmedId?: string | null;
  storefrontOrigin?: string | null;
  storeHash?: string | null;
  customUrl?: string | null;
  sellingPrice?: ListingChannelPriceSummary;
}

/** Narrow/mobile presentation of a single listing — same canonical state, no second data model. */
export default function ListingCard({ listing: l, readiness: rdy, confirmedId, storefrontOrigin, storeHash, customUrl, sellingPrice }: ListingCardProps) {
  const liveId = confirmedId ?? l.external_listing_id;
  const approval = approvalState(l);
  const connectionReady = connectorIsReady(l.connector?.auth_status);
  const operatorDirty = operatorDirtyDomains(l.dirty_domains);
  const action = nextAction({
    readiness: rdy,
    approval,
    status: l.status,
    publicationStatus: l.publication_status,
    hasDirty: operatorDirty.length > 0,
    dirtyDomains: l.dirty_domains,
    connectionReady,
    providerConfirmedListed: Boolean(liveId),
  });

  const approvalCls = approvalTone(approval) === "approved"
    ? "bg-emerald-50 text-emerald-700"
    : approvalTone(approval) === "warning"
    ? "bg-amber-50 text-amber-700"
    : "bg-slate-100 text-slate-600";

  const signal = publicationStateMeta({
    publicationStatus: l.publication_status,
    status: l.status,
        hasDirty: operatorDirty.length > 0,
    connectionReady,
    lastError: l.last_error,
    providerConfirmedListed: Boolean(liveId),
  });
  const pubTone = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    grey: "text-slate-400",
  }[signal.signal];

  const actionTone = action?.tone ?? "neutral";
  const actionCls = {
    danger: "text-red-600",
    warning: "text-amber-600",
    primary: "text-emerald-600",
    neutral: "text-slate-500",
  }[actionTone];
  const actionIcon = {
    danger: "ri-alert-line",
    warning: "ri-arrow-right-line",
    primary: "ri-store-2-line",
    neutral: "ri-arrow-right-line",
  }[actionTone];

  const readinessCls = {
    ready: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    blocked: "bg-red-50 text-red-700",
    neutral: "bg-slate-100 text-slate-500",
  }[readinessTone(rdy)];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {canonicalListingProductId(l) ? (
            <Link to={`/products/${canonicalListingProductId(l)}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 truncate block">
              {l.product?.name ?? "—"}
            </Link>
          ) : (
            <span className="text-sm font-medium text-slate-800 truncate block">{l.product?.name ?? "—"}</span>
          )}
          <span className="text-xs text-slate-400 font-mono">{l.product?.sku ?? "—"}</span>
        </div>
          <span className="text-xs text-slate-400 shrink-0">{providerLabel(l.connector?.provider)}</span>
        </div>
        {sellingPrice && (
          <Link to={`/listings/${l.id}?tab=pricing`} className="block">
            <p className="text-sm text-slate-800">{sellingPrice.label}</p>
            {sellingPrice.overrideCount > 0 && (
              <p className="text-[11px] text-amber-700">{sellingPrice.overrideCount === 1 ? "Variant override" : `${sellingPrice.overrideCount} overrides`}</p>
            )}
            {sellingPrice.pendingLabel && <p className="text-[11px] text-amber-700">{sellingPrice.pendingLabel}</p>}
          </Link>
        )}

      <div className="flex flex-wrap items-center gap-1.5">
        {rdy ? (
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${readinessCls}`}>
            {readinessLabel(rdy)}
          </span>
        ) : (
          <span className="text-xs text-slate-400">Not evaluated</span>
        )}
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${approvalCls}`}>{approvalLabel(approval)}</span>
        <span className="inline-flex items-center gap-1 text-xs">
          <i className={`${signal.icon} ${pubTone}`} />
          <span className={`font-medium ${pubTone}`}>{signal.label}</span>
          <ViewLiveLink href={listingLiveLinks({
            provider: l.connector?.provider,
            externalId: liveId,
            storefrontOrigin,
            customUrl,
          }).liveUrl} />
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <span className="text-xs text-slate-400 min-w-0 truncate">
          {connectorDisplayName({
            id: l.connector_id,
            name: l.connector?.name ?? "",
            provider: l.connector?.provider ?? null,
            authStatus: l.connector?.auth_status,
            storefrontOrigin,
            storeHash,
          })}
          {l.last_published_at ? ` · ${new Date(l.last_published_at).toLocaleDateString()}` : ""}
        </span>
        {action ? (
          <Link to={`/listings/${l.id}`} className={`inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap shrink-0 ${actionCls}`}>
            <i className={actionIcon} />
            {action.label}
          </Link>
        ) : (
          <Link to={`/listings/${l.id}`} className="text-xs font-medium text-slate-400 whitespace-nowrap shrink-0">
            View
          </Link>
        )}
      </div>
    </div>
  );
}